import React from "react";
import { FormComponent } from "../../../../../react-core/Html/FormComponent";
import { SelectEditor } from "../../../../../react-core/Html/SelectEditor";
import { TextBoxEditor } from "../../../../../react-core/Html/TextBoxEditor";
import { AlphanumericWithSpecialCharacters } from "../../../../../react-core/Html/Validation/RegExPatterns";
import { CompanyType } from "../../../../../Wrap/Constants/Enums/Customer/CompanyType";
import { CustomerType } from "../../../../../Wrap/Constants/Enums/Customer/CustomerType";
import { CustomerStatus } from "../../../../../Wrap/Constants/Enums/Domain/CustomerStatus";
import { AccountRoleByActorDetails } from "../../../../../Wrap/Models/Domains/Account/AccountRoleByActorDetails";
import { Company } from "../../../../../Wrap/Models/Models/Customer/Company";
import { CompanyTypeMap, CompanyTypeOption, CustomerTypeArraysMap } from "../AddClient/Resource/Contents";
import { ClientRecordAction } from "./Action/ClientRecordAction";
import { SectionTable } from "./Components/SectionTable";
import { ClientRecordTab, customerStatusMap, IClientTabProps } from "./Resource/Contents";
import { AccountRoleStore, ClientRecordStore } from "./Store/Variable";

interface ICompanyDetailState {
    companyDetail: Company;
    isCompanyDetailLoading: boolean;
    accountRoleDetails: Array<AccountRoleByActorDetails>;
}

export class CompanyDetails extends FormComponent<IClientTabProps, ICompanyDetailState> {
    constructor(props: IClientTabProps) {
        super(props);
        this.state = {
            companyDetail: null,
            isCompanyDetailLoading: true,
            accountRoleDetails: AccountRoleStore.getData()?.length > 0 ? AccountRoleStore.getData() : null,
        };
        this.subscribeToStore(ClientRecordStore, () => this.onStoreChange());
        this.subscribeToStore(AccountRoleStore, () => this.onAccountRoleStoreChange());
    }

    componentDidMount() {
        this.setState({ isCompanyDetailLoading: true });
        ClientRecordAction.getCompanyDetail(this.props.id);
    }

    private onStoreChange() {
        const data = ClientRecordStore.getData();
        if (data[ClientRecordTab.CompanyDetails]) {
            const company = data[ClientRecordTab.CompanyDetails];
            this.setState({
                isCompanyDetailLoading: false,
                companyDetail: company,
            });
        }
    }

    private onAccountRoleStoreChange() {
        const data = AccountRoleStore.getData();
        if (data.length > 0) {
            this.setState({ accountRoleDetails: data });
        }
    }

    private onCompanyDetailSave(data) {
        if (this.isValid()) {
            this.setState({ isCompanyDetailLoading: true });
            ClientRecordAction.putCompanyDetail(data, this.props.id);
            return true;
        }
        return false;
    }

    private getCompanyDetailColConfig(data: Company, roles: Array<AccountRoleByActorDetails>) {
        if (!data) {return [];}
        const config = [
            {
                title: "Organisation type",
                data: CustomerTypeArraysMap[CustomerType.Company],
            },
            {
                title: "Organisation status",
                data:
                    data.Status == CustomerStatus.Inactive
                        ? "Inactive"
                        : roles.length
                        ? customerStatusMap[data.Status]
                        : "Prospect",
            },
            {
                title: "Organisation name",
                data: data?.CompanyName,
            },
            {
                title: "Company type",
                data: CompanyTypeMap[data?.CompanyType],
            },
        ];
        return config;
    }

    private renderCompanyDetailLightBox(data: Company, onDataChange) {
        return (
            <>
                <TextBoxEditor
                    cssWrapper="control-size-430"
                    value={data?.CompanyName}
                    label={"Name"}
                    regex={{
                        errorMessage: "Only alphanumeric and special characters allowed.",
                        value: AlphanumericWithSpecialCharacters,
                    }}
                    onChange={(value) => onDataChange({ CompanyName: value })}
                    required={"Please enter name"}
                    maxLength={50}
                />
                <SelectEditor
                    options={CompanyTypeOption}
                    getText={(v) => {
                        return CompanyTypeMap[v];
                    }}
                    cssWrapper="control-size-430"
                    required="Please select"
                    label="Company type"
                    validationRules={[
                        {
                            errorMessage: "Please select",
                            validator: (v) => {
                                return v != CompanyType.NotSpecified;
                            },
                        },
                    ]}
                    selected={data?.CompanyType}
                    onChange={(value) => onDataChange({ CompanyType: value })}
                />
            </>
        );
    }

    render() {
        const { companyDetail, isCompanyDetailLoading, accountRoleDetails } = this.state;

        return (
            <div className="panel-subtle pl-34 pt-38">
                <SectionTable
                    isLoading={isCompanyDetailLoading}
                    sectionHeader={"Company details"}
                    columnConfigs={this.getCompanyDetailColConfig(companyDetail, accountRoleDetails || [])}
                    lightboxContent={(data, onDataChange) => this.renderCompanyDetailLightBox(data, onDataChange)}
                    onSave={(data) => this.onCompanyDetailSave(data)}
                    data={companyDetail}
                    sectionHeaderCss={"mb-40"}
                    cssWrapper={"mb-20"}
                    canEdit={this.props?.hasReadWriteAccess == true}
                />
            </div>
        );
    }
}
